import { getFromDataPath, mutateByPath, reduceDataPath } from "../../../utility";
import { ArsSidebarItemKind } from "../../sidebar";
import { isAnalysisMethod, isGroupingFactor, isOperation } from "../../typeGuards";
import {
  ARST_INSERT_COLUMN_AFTER,
  ARST_INSERT_COLUMN_BEFORE,
  ARST_INSERT_OPERATION_COLUMN_AFTER,
  ARST_INSERT_OPERATION_COLUMN_BEFORE,
  ARST_INSERT_OPERATION_ROW_AFTER,
  ARST_INSERT_OPERATION_ROW_BEFORE,
  ARST_INSERT_OPERATION_SUBGROUP,
  ARST_INSERT_ROW_AFTER,
  ARST_INSERT_ROW_BEFORE,
  ARST_INSERT_SUBGROUP,
  ArsTableGroupCalculationDto,
  ArsTableState,
  ArstActionDropAction,
} from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import {
  arst_analysisMethodToTableGroup,
  arst_groupingFactorToTableGroup,
  arst_operationToTableGroup,
} from "./arst_arsToTableGroup";
import { arst_checkRowHeaders } from "./arst_checkRowHeaders";
import { arst_resolveTargetPath } from "./arst_resolveTargetPath";

/**
 * Inserts dropped grouping factors into the column hierarchy.
 *
 * @param state - Current ARS table state
 * @param action - Drop action describing the target column position
 * @returns Updated ARS table state
 */
export const arst_insertDroppedItem = <T>(
  state: ArsTableState<T>,
  action: ArstActionDropAction,
): ArsTableState<T> => {
  const targetCell = state.scaffoldModel?.cellData?.[action.rowId]?.[action.columnId];
  if (!targetCell) return state;

  const targetDataPath = arst_resolveTargetPath(targetCell.dataPath);

  // Create new table groups from the dropped item
  const newTableGroups: ArsTableGroupCalculationDto<T>[] = action.payload
    .map((item) => item.dropItem)
    .map((item) => {
      return isGroupingFactor(item)
        ? arst_groupingFactorToTableGroup(item, state.settings.editorProps)
        : isOperation(item)
          ? arst_operationToTableGroup(item, state.settings.editorProps)
          : isAnalysisMethod(item)
            ? arst_analysisMethodToTableGroup(item, state.settings.editorProps)
            : null;
    })
    .filter((group): group is ArsTableGroupCalculationDto<T> => group !== null);
  if (newTableGroups.length === 0) return state;

  // Update AnalysisMethodId
  const newAnalysisMethodIds = new Set<string>(
    [
      state.bodyData.analysisMethodId,
      ...action.payload
        .filter((item) => item.kind === "analysisMethod")
        .map((item) => item.dropItem.id),
      ...action.payload
        .filter((item) => item.kind === "operation")
        .map((item) => item.analysisMethodId),
    ].filter(Boolean),
  );
  if (newAnalysisMethodIds.size > 1) {
    // Abort if multiple analysis methods are being inserted or if the new analysis method is different from the current one
    return state;
  }

  // Insert subGroup under target group
  /* istanbul ignore else */
  if ([ARST_INSERT_SUBGROUP, ARST_INSERT_OPERATION_SUBGROUP].includes(action.operation)) {
    let newBodyData = state.bodyData;
    // Get target group
    const currentGroup = getFromDataPath(targetDataPath, newBodyData, (obj) =>
      isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
    );
    // Abort if target is not found
    if (!currentGroup) return state;
    // Get new subGroups for the current group
    const newSubGroups = [...(currentGroup.subGroups ?? []), ...newTableGroups];
    newBodyData = mutateByPath(newBodyData, `${targetDataPath}/subGroups`, newSubGroups);
    const newGroupingFactorIds = new Set(state.usedGroupingFactorIds);
    action.payload.forEach((item) => {
      // TODO: This shouldn't be here, there should an an operation test!
      /* istanbul ignore else */
      if (item.kind === ArsSidebarItemKind.GroupingFactor) {
        newGroupingFactorIds.add(item.dropItem.id);
      }
    });
    newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);
    return {
      ...state,
      bodyData: {
        ...newBodyData,
        analysisMethodId:
          newAnalysisMethodIds.values().next().value ??
          // istanbul ignore next
          "",
      },
      usedGroupingFactorIds: newGroupingFactorIds,
      changed: true,
    };
  }

  // Insert as a sibling to target group
  else if (
    [
      ARST_INSERT_COLUMN_AFTER,
      ARST_INSERT_COLUMN_BEFORE,
      ARST_INSERT_ROW_AFTER,
      ARST_INSERT_ROW_BEFORE,
      ARST_INSERT_OPERATION_COLUMN_BEFORE,
      ARST_INSERT_OPERATION_COLUMN_AFTER,
      ARST_INSERT_OPERATION_ROW_BEFORE,
      ARST_INSERT_OPERATION_ROW_AFTER,
    ].includes(action.operation)
  ) {
    // Set up return
    let newBodyData = state.bodyData;
    // Resolve if before or after
    const isInsertAfter = [
      ARST_INSERT_COLUMN_AFTER,
      ARST_INSERT_ROW_AFTER,
      ARST_INSERT_OPERATION_COLUMN_AFTER,
      ARST_INSERT_OPERATION_ROW_AFTER,
    ].includes(action.operation);
    // Resolve if column or row
    const isInsertColumn = [
      ARST_INSERT_COLUMN_AFTER,
      ARST_INSERT_COLUMN_BEFORE,
      ARST_INSERT_OPERATION_COLUMN_AFTER,
      ARST_INSERT_OPERATION_COLUMN_BEFORE,
    ].includes(action.operation);

    // Get path parts here
    const targetPathParts = targetDataPath.split("/").filter(Boolean);

    // Inserting at start of root
    if (targetPathParts.length === 0 || targetPathParts[0] === "rowHeaders") {
      const newRootGroups = isInsertColumn
        ? [...newTableGroups, ...(state.bodyData.columns ?? [])]
        : [...newTableGroups, ...(state.bodyData.rows ?? [])];
      const newBodyData = isInsertColumn
        ? { ...state.bodyData, columns: newRootGroups }
        : { ...state.bodyData, rows: newRootGroups };
      newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);
      return {
        ...state,
        bodyData: {
          ...newBodyData,
          analysisMethodId: newAnalysisMethodIds.values().next().value ?? "",
        },
        changed: true,
      };
    } else {
      // Update inserting index if inserting after
      const thisIndex = parseInt(targetPathParts[targetPathParts.length - 1], 10);
      const insertAt = thisIndex + (isInsertAfter ? 1 : 0);
      if (Number.isNaN(thisIndex)) return state;
      if (isInsertAfter) {
        targetPathParts[targetPathParts.length - 1] = insertAt.toString();
      }
      const insertDataPath = `/${targetPathParts.join("/")}`;

      // Update table groups, either rows or columns
      const parentPath = reduceDataPath(insertDataPath, 2);
      const parentGroup = getFromDataPath(parentPath, newBodyData, (obj) =>
        isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
      );
      if (parentGroup) {
        const newSubGroups = [...(parentGroup.subGroups ?? [])];
        newSubGroups.splice(insertAt, 0, ...newTableGroups);
        newBodyData = mutateByPath(newBodyData, `${parentPath}/subGroups`, newSubGroups);
      } else {
        // Inserting at root level
        if (isInsertColumn) {
          const newColumns = [...(state.bodyData.columns ?? [])];
          newColumns.splice(insertAt, 0, ...newTableGroups);
          newBodyData = { ...newBodyData, columns: newColumns };
        } else {
          const newRows = [...(state.bodyData.rows ?? [])];
          newRows.splice(insertAt, 0, ...newTableGroups);
          newBodyData = { ...newBodyData, rows: newRows };
        }
      }

      // Update rowHeaders if required
      newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);
    }

    // Update used grouping factor ids
    const newUsedGroupingFactorIds = new Set(state.usedGroupingFactorIds);
    action.payload.forEach((item) => {
      if (item.kind === ArsSidebarItemKind.GroupingFactor) {
        newUsedGroupingFactorIds.add(item.dropItem.id);
      }
    });

    return {
      ...state,
      bodyData: {
        ...newBodyData,
        analysisMethodId: newAnalysisMethodIds.values().next().value ?? "",
      },
      usedGroupingFactorIds: newUsedGroupingFactorIds,
      changed: true,
    };
  }
  // istanbul ignore next - should not happen
  return state;
};
